import { useAvatarImageUrl } from '@/hooks';
import { Avatar } from '@/components/ui';

export interface ProfileAvatarProps {
  /** Storage path returned by the profile-image-upload edge function. */
  imagePath?: string | null;
  /**
   * Direct URL (e.g. `blob:`, `data:`, or any http URL) that bypasses the
   * signed-URL resolver. Use for local previews before a file is uploaded.
   * Takes precedence over `imagePath`.
   */
  directUrl?: string | null;
  /** Emoji fallback when no image is set (or while the signed URL loads). */
  emoji?: string | null;
  /**
   * Background color for the emoji fallback. Accepts a Tailwind `bg-*`
   * token class OR a literal color value (hex/rgb) for inline style.
   */
  color?: string | null;
  /** Shown only when neither an image nor an emoji is available. */
  initials?: string;
  alt?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

/**
 * Renders an avatar that prefers an uploaded image (resolved from a storage
 * path via signed URL) and falls back to the emoji+color or initials.
 *
 * All user/household/child avatar surfaces should use this component so the
 * emoji ↔ image swap happens in one place. If you pass a path that resolves
 * to null (forbidden / deleted), we render the emoji fallback instead of a
 * broken image.
 */
export function ProfileAvatar({
  imagePath,
  directUrl,
  emoji,
  color,
  initials,
  alt = '',
  size = 'md',
  className = '',
}: ProfileAvatarProps) {
  // Skip the signed-URL resolver when a direct URL is provided — it's
  // meant for local previews (blob: / data:) that the edge function can't
  // resolve anyway.
  const { url, loading } = useAvatarImageUrl(
    directUrl ? null : (imagePath ?? null),
  );
  const resolvedUrl = directUrl ?? url;

  // Normalize color prop: classes starting with "bg-" go through className,
  // hex/rgb goes through Avatar's style-based `color` prop.
  const isBgClass = typeof color === 'string' && color.startsWith('bg-');
  const mergedClassName = [className, isBgClass ? color : null]
    .filter(Boolean)
    .join(' ');
  const inlineColor = !isBgClass ? (color ?? undefined) : undefined;

  // While a signed URL is still in flight, show a neutral placeholder
  // instead of flashing the emoji fallback — otherwise the emoji renders
  // for ~200ms and then swaps to the real image, which feels broken.
  if (imagePath && !directUrl && loading) {
    return (
      <Avatar
        loading
        size={size}
        className={className}
        alt={alt}
      />
    );
  }

  if (resolvedUrl) {
    return (
      <Avatar
        src={resolvedUrl}
        alt={alt}
        size={size}
        className={className}
      />
    );
  }

  if (emoji) {
    return (
      <Avatar
        emoji={emoji}
        color={inlineColor ?? undefined}
        alt={alt || emoji}
        size={size}
        className={mergedClassName}
      />
    );
  }

  return (
    <Avatar
      initials={initials}
      alt={alt || initials}
      size={size}
      className={className}
    />
  );
}
